import { Stack } from "expo-router";
import { useRef } from "react";
import { View } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { useUnistyles } from "react-native-unistyles";
import { AppPersistQueryClientProvider } from "@src/features/query-client";
import { FontsProvider, resolveFontFamily } from "@src/providers/fonts-provider";
import { FontFamilyProvider } from "@visioni/a3s-ui/components";
import { IconProvider, iconRegistry } from "@visioni/a3s-ui/icons";
import { AnalyticsProvider } from "@src/providers/analytics-provider";
import { AuthProvider } from "@src/features/auth";
import { LoadingScreen } from "@src/features/auth/components";
import { UserProvider, AppNotConfiguredView } from "@src/features/tenants";
import { SystemBars } from "react-native-edge-to-edge";
import { KeyboardProvider } from "react-native-keyboard-controller";
import "react-native-reanimated";
import { useHolidayReminderModal } from "@src/hooks/useHolidayReminderModal";
import { useLegacyAppMode } from "@src/hooks/useLegacyAppMode";
import type { UseLegacyAppModeReturn } from "@src/hooks/useLegacyAppMode";
import { useAppBoot } from "@src/hooks/useAppBoot";
import { HolidayReminderModal } from "@src/components/HolidayReminderModal";
import { ForceUpgradeModal } from "@src/components/ForceUpgradeModal";
import { useForceUpgrade } from "@src/hooks/useForceUpgrade";
import { useForceLogout } from "@src/hooks/useForceLogout";
import { Toaster } from "@src/components/Toaster";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import * as SplashScreen from "expo-splash-screen";
import { TenantProvider, useTenant } from "@src/features/tenants";
import { DevToolsProvider } from "@src/providers/dev-tools-provider";
import { usePushNotifications } from "@src/features/notifications";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { ImpersonationBanner } from "@src/features/auth/components/ImpersonationBanner";

SplashScreen.preventAutoHideAsync();

type BootCoordinatorProps = Pick<
  UseLegacyAppModeReturn,
  "setShouldShowReactNativeScreens" | "setFeatureFlagsFailedToLoad" | "handlePresentLegacyApp"
> & {
  checkForceUpgrade: () => void;
};

function BootCoordinator({
  setShouldShowReactNativeScreens,
  setFeatureFlagsFailedToLoad,
  handlePresentLegacyApp,
  checkForceUpgrade,
}: BootCoordinatorProps) {
  const { isResolved: isTenantResolved } = useTenant();

  useAppBoot({
    setShouldShowReactNativeScreens,
    setFeatureFlagsFailedToLoad,
    handlePresentLegacyApp,
    isTenantResolved,
    checkForceUpgrade,
  });

  return null;
}

// Requires to be nested within QueryClientProvider
function PushNotificationManager() {
  const { getBooleanFlag } = useFeatureFlags();
  usePushNotifications({ enabled: getBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS) });
  return null;
}

export default function RootLayout() {
  const showHolidayReminderRef = useRef(false);

  const {
    showModal: showHolidayReminder,
    setShowModal: setShowHolidayReminder,
    hireButtonLoading,
    handleHolidayModalHireCamera,
    handleRemindLater,
    handleModalDismiss,
  } = useHolidayReminderModal(showHolidayReminderRef);

  const {
    shouldShowReactNativeScreens,
    setShouldShowReactNativeScreens,
    featureFlagsFailedToLoad,
    setFeatureFlagsFailedToLoad,
    handleRetryFeatureFlags,
    handlePresentLegacyApp,
  } = useLegacyAppMode({ showHolidayReminderRef });

  const { showForceUpgrade, forceUpgradeConfig, checkForceUpgrade } = useForceUpgrade();
  useForceLogout();

  const { theme } = useUnistyles();
  const backgroundColor = theme.colors.background as string;

  const stackContent = (
    <>
      {shouldShowReactNativeScreens === null ?
        <LoadingScreen message="Loading..." />
      : shouldShowReactNativeScreens === false ?
        <View style={{ flex: 1, backgroundColor }} />
      : featureFlagsFailedToLoad ?
        <AppNotConfiguredView onRetry={() => void handleRetryFeatureFlags()} />
      : <>
          <Stack
            screenOptions={{
              headerStyle: {
                backgroundColor: theme.colors.background,
              },
              headerTintColor: theme.colors.text,
              headerBackButtonDisplayMode: "minimal",
              contentStyle: {
                backgroundColor: theme.colors.background,
              },
            }}
          >
            <Stack.Screen name="index" options={{ headerShown: false }} />
            <Stack.Screen
              name="(tabs)"
              options={{
                headerShown: false,
              }}
            />
            <Stack.Screen name="development/theme-demo" options={{ title: "Theme Demo" }} />
            <Stack.Screen name="development/components/toast" options={{ title: "Toast" }} />
            <Stack.Screen name="development/feature-flags" options={{ title: "Feature Flags" }} />
            <Stack.Screen name="development/environment" options={{ title: "Environment" }} />
            <Stack.Screen
              name="agent"
              options={({ route }: { route: { params?: { title?: string } } }) => ({
                title: route.params?.title ?? "AI Schedule Assistant",
              })}
            />
            <Stack.Screen name="settings/tenant-select" options={{ title: "Select Organisation" }} />
            <Stack.Screen name="auth/login" options={{ title: "Login", headerShown: false }} />
            <Stack.Screen name="auth/forgot-password" options={{ title: "Forgot Password", headerShown: false }} />
            <Stack.Screen name="auth/biometrics" options={{ title: "Biometrics", headerShown: false }} />
            <Stack.Screen name="auth/biometric-verify" options={{ title: "Verify", headerShown: false }} />
            <Stack.Screen name="auth/pin" options={{ title: "Pin", headerShown: false }} />
            <Stack.Screen name="auth/confirm-pin" options={{ title: "Confirm Pin", headerShown: false }} />
            <Stack.Screen name="auth/insert-pin" options={{ title: "Insert Pin", headerShown: false }} />
            <Stack.Screen name="auth/change-pin" options={{ title: "Change Pin", headerShown: false }} />
            <Stack.Screen
              name="onboarding/push-notifications"
              options={{ title: "Notifications", headerShown: false }}
            />
            <Stack.Screen name="tenants/[tenantId]" options={{ headerShown: false }} />
            <Stack.Screen name="settings/users-and-permissions" options={{ title: "Users & Permissions" }} />
            <Stack.Screen name="settings/notification-preferences" options={{ title: "Notification Preferences" }} />
            <Stack.Screen name="not-found" options={{ title: "Not Found", headerShown: false }} />
            <Stack.Screen name="+not-found" options={{ title: "Not Found", headerShown: false }} />
          </Stack>
        </>
      }
    </>
  );

  return (
    <BackgroundGradient>
      <GestureHandlerRootView
        style={{
          flex: 1,
          backgroundColor: theme.colors.background,
        }}
        testID="screen-root"
      >
        <KeyboardProvider>
          <FontsProvider>
            <FontFamilyProvider resolve={resolveFontFamily}>
              <IconProvider registry={iconRegistry}>
                <AppPersistQueryClientProvider>
                  <DevToolsProvider>
                    <UserProvider>
                      <TenantProvider>
                        <BootCoordinator
                          setShouldShowReactNativeScreens={setShouldShowReactNativeScreens}
                          setFeatureFlagsFailedToLoad={setFeatureFlagsFailedToLoad}
                          handlePresentLegacyApp={handlePresentLegacyApp}
                          checkForceUpgrade={checkForceUpgrade}
                        />
                        <BottomSheetModalProvider>
                          <ActionSheetProvider>
                            <AuthProvider shouldShowReactNativeScreens={shouldShowReactNativeScreens}>
                              <PushNotificationManager />
                              <AnalyticsProvider>
                                <View style={{ flex: 1 }}>
                                  <ImpersonationBanner />
                                  {stackContent}
                                </View>
                              </AnalyticsProvider>
                            </AuthProvider>
                          </ActionSheetProvider>
                        </BottomSheetModalProvider>
                        <SystemBars style="auto" />
                        <Toaster />
                      </TenantProvider>
                    </UserProvider>
                  </DevToolsProvider>
                </AppPersistQueryClientProvider>
                <HolidayReminderModal
                  visible={showHolidayReminder}
                  onClose={() => setShowHolidayReminder(false)}
                  onDismiss={handleModalDismiss}
                  onHireCamera={handleHolidayModalHireCamera}
                  onRemindLater={handleRemindLater}
                  hireButtonLoading={hireButtonLoading}
                />
                <ForceUpgradeModal
                  visible={showForceUpgrade}
                  upgradeMessage={forceUpgradeConfig.upgradeMessage}
                  appStoreUrl={forceUpgradeConfig.appStoreUrl}
                  playStoreUrl={forceUpgradeConfig.playStoreUrl}
                />
              </IconProvider>
            </FontFamilyProvider>
          </FontsProvider>
        </KeyboardProvider>
      </GestureHandlerRootView>
    </BackgroundGradient>
  );
}
